/**
 * Building the answer scale, and reading it back.
 *
 * Native radio inputs inside a `fieldset` with a `legend`. No ARIA, no
 * keyboard handling, no custom widget: a screen reader announces "Statement 2
 * of 3, Agree, radio button, 4 of 5" from the markup alone, arrow keys move
 * within the group, and the whole thing works before the stylesheet loads.
 * Every custom implementation of this control is worse than the built-in one.
 */

export interface QuestionSpec {
  /** Distinct within one form; used as the radio group's `name`. */
  key: string;
  text: string;
}

export interface RenderOptions {
  /** In the order the statements are numbered, which is the order answers are stored in. */
  specs: QuestionSpec[];
  labels: string[];
  /** One entry per spec: a previously chosen position, or null. */
  initial: (number | null)[];
  /**
   * The indices of {@link specs}, in the order this participant sees them.
   *
   * Display order and storage order are separated here and nowhere else. A
   * radio group is found by its `name` and a fieldset by its `data-key`, never
   * by position, so everything downstream — reading the answers back, marking
   * what is missing, restoring a draft — is indifferent to what this says.
   */
  order: readonly number[];
  onChange: () => void;
}

/**
 * Text is set with `textContent` throughout, never by assembling HTML.
 * Statement wording comes from the organizer, who is trusted with far more
 * than this — but "trusted input" is how injection arrives, and the alternative
 * costs nothing.
 */
export function renderQuestions(container: HTMLElement, options: RenderOptions): void {
  // The order has to name every question exactly once. It always does — it is
  // built from `statementOrder`, which is total — but the failure mode if it
  // ever did not is the worst kind on this page: a dropped question cannot be
  // answered, cannot be marked as missing because it has no fieldset, and so
  // shows as a Submit button that silently does nothing. Checked rather than
  // assumed, on the same grounds as `must`.
  const ordered: { spec: QuestionSpec; index: number }[] = [];
  for (const index of options.order) {
    const spec = options.specs[index];
    if (spec === undefined) {
      throw new Error(`The display order names question ${index}, which does not exist.`);
    }
    ordered.push({ spec, index });
  }
  if (ordered.length !== options.specs.length || new Set(options.order).size !== ordered.length) {
    throw new Error('The display order must name every question exactly once.');
  }

  container.replaceChildren();

  for (const { spec, index } of ordered) {
    const fieldset = document.createElement('fieldset');
    fieldset.className = 'question';
    fieldset.dataset['key'] = spec.key;

    const legend = document.createElement('legend');
    legend.className = 'question__text';
    legend.textContent = spec.text;
    fieldset.append(legend);

    const scale = document.createElement('div');
    scale.className = 'scale';
    options.labels.forEach((label, position) => {
      const value = position + 1;
      const option = document.createElement('label');
      option.className = 'option';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = spec.key;
      input.value = String(value);
      input.className = 'option__input';
      if (options.initial[index] === value) input.checked = true;
      input.addEventListener('change', () => {
        fieldset.classList.remove('question--missing');
        error.hidden = true;
        options.onChange();
      });

      const caption = document.createElement('span');
      caption.className = 'option__label';
      caption.textContent = label;

      option.append(input, caption);
      scale.append(option);
    });
    fieldset.append(scale);

    const error = document.createElement('p');
    error.className = 'question__error';
    error.textContent = 'Please choose one.';
    error.hidden = true;
    fieldset.append(error);

    container.append(fieldset);
  }
}

export function readAnswers(container: HTMLElement, specs: QuestionSpec[]): (number | null)[] {
  return specs.map((spec) => {
    const chosen = container.querySelector<HTMLInputElement>(`input[name="${spec.key}"]:checked`);
    return chosen === null ? null : Number(chosen.value);
  });
}

/**
 * Mark every unanswered question and return the topmost of them, so the caller
 * can move focus there.
 *
 * Focus moves rather than the page merely turning red, because on a phone the
 * unanswered question is usually off-screen — a participant who taps Submit
 * and sees nothing happen concludes the button is broken.
 *
 * "Topmost" is read back out of the container rather than taken as the first
 * unanswered `spec`. The two are the same only when the statements are shown in
 * the order they are numbered; with the order varied per participant, going by
 * `specs` would scroll somebody past an unanswered question to reach a lower
 * one. The DOM is the only thing that knows what is above what.
 */
export function markMissing(
  container: HTMLElement,
  specs: QuestionSpec[],
  answers: (number | null)[],
): HTMLElement | null {
  specs.forEach((spec, index) => {
    if (answers[index] !== null) return;
    const fieldset = container.querySelector<HTMLElement>(`fieldset[data-key="${spec.key}"]`);
    if (fieldset === null) return;
    fieldset.classList.add('question--missing');
    const error = fieldset.querySelector<HTMLElement>('.question__error');
    if (error !== null) error.hidden = false;
  });
  return container.querySelector<HTMLElement>('.question--missing');
}
